import { getStore } from '@core/store';
import constants from './constants';

/**
 * Centralized helper for authenticated API requests
 * Ensures userId is always included in requests to prevent 401 errors
 *
 * @param {Object} bus - Event bus instance
 * @param {Object} config - Request configuration
 * @param {string} config.id - Unique request ID
 * @param {string} config.url - Request URL
 * @param {string} config.method - HTTP method (GET, POST, PUT, DELETE)
 * @param {Object} config.data - Request body data (for POST/PUT)
 * @param {boolean} config.requiresAuth - Whether auth is required (default: true)
 * @param {string} config.errorEvent - Event to emit on auth error (optional)
 * @returns {boolean|null} - false if auth failed, null otherwise
 */
export function authenticatedRequest(bus, config) {
  const {
    id,
    url,
    method,
    data,
    requiresAuth = true,
    errorEvent
  } = config;

  // For public endpoints, just emit the request as-is
  if (requiresAuth === false) {
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id,
      url,
      method,
      data
    });
    return null;
  }

  // Get userId from user store
  const userStore = getStore('user');
  const userId = userStore?.state?.current?.id;

  if (!userId) {
    const errorMessage = 'Authentication required: please log in';

    // Show toast notification
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Authentication requise',
      body: 'Vous devez être connecté pour effectuer cette action'
    });

    // Emit error event if provided
    if (errorEvent) {
      bus.emit(errorEvent, { message: errorMessage });
    }

    return false;
  }

  // Build request config with userId
  const requestConfig = {
    id,
    method
  };

  // Add userId and group based on HTTP method
  if (method === 'GET' || method === 'DELETE') {
    // For GET/DELETE, add userId and group as query parameters
    requestConfig.url = `${url}`;
  } else {
    // For POST/PUT/PATCH, add userId and group to body
    requestConfig.url = url;
    requestConfig.data = {
      ...(data || {}),
      userId,
      group: 'ultraxeno'
    };
  }

  // Emit the authenticated request
  bus.emit(constants.EVENTS.NETWORK.REQUEST, requestConfig);

  return null;
}

/**
 * Wrapper for GET requests with authentication
 */
export function authenticatedGet(bus, url, options = {}) {
  return authenticatedRequest(bus, {
    ...options,
    url,
    method: 'GET'
  });
}

/**
 * Wrapper for POST requests with authentication
 */
export function authenticatedPost(bus, url, data, options = {}) {
  return authenticatedRequest(bus, {
    ...options,
    url,
    method: 'POST',
    data
  });
}

/**
 * Wrapper for PUT requests with authentication
 */
export function authenticatedPut(bus, url, data, options = {}) {
  return authenticatedRequest(bus, {
    ...options,
    url,
    method: 'PUT',
    data
  });
}

/**
 * Wrapper for DELETE requests with authentication
 */
export function authenticatedDelete(bus, url, options = {}) {
  return authenticatedRequest(bus, {
    ...options,
    url,
    method: 'DELETE'
  });
}

/**
 * Get current userId from store
 * Returns null if not authenticated
 */
export function getUserId() {
  const userStore = getStore('user');
  return userStore?.state?.current?.id || null;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return getUserId() !== null;
}
